import useSidebarState from '../../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { onValue, ref as dbRef } from 'firebase/database';
import Sidebar from '../Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { database } from '../../firebase';
import useTeamAccessProfile from '../../hooks/useTeamAccessProfile';
import useCompanyUID from '../../hooks/useCompanyUID';
import CommunityPostComposer from './CommunityPostComposer';
import { buildCommunityChannelOptions, normalizeCommunityChannels } from '../../utils/teamAccess';
import './Community.css';

const CommunityPublishPage = () => {
  useUiLanguage();
  const { t } = useTranslation('community');
  const { isRTL } = useLanguage();
  const { currentUser } = useAuth();
  const { companyUID } = useCompanyUID();
  const teamAccess = useTeamAccessProfile(companyUID);
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [successMessage, setSuccessMessage] = useState('');
  const [channels, setChannels] = useState({});
  const [channelLoading, setChannelLoading] = useState(true);

  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  useEffect(() => {
    if (!companyUID) {
      setChannelLoading(false);
      return undefined;
    }

    const unsubscribe = onValue(dbRef(database, `companies/${companyUID}/community/channels`), (snapshot) => {
      setChannels(snapshot.exists() ? snapshot.val() || {} : {});
      setChannelLoading(false);
    });

    return () => unsubscribe();
  }, [companyUID]);

  const channelOptions = useMemo(
    () => buildCommunityChannelOptions(channels),
    [channels]
  );
  const enabledChannelIds = useMemo(
    () => channelOptions.map((channel) => channel.value),
    [channelOptions]
  );
  const availableFeeds = teamAccess.isCompanyOwner
    ? enabledChannelIds
    : normalizeCommunityChannels(teamAccess.allowedCommunityChannels, enabledChannelIds);
  const canEditCommunity = teamAccess.isCompanyOwner || (teamAccess.userPermissions.canEditCommunity && availableFeeds.length > 0);
  const accessLoading = teamAccess.loading || channelLoading;

  return (
    <div className="community-shell">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      <div
        className="community-body"
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          marginInlineStart: sidebarWidth,
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarWidth})`,
        }}
      >
        {accessLoading ? (
          <div className="community-public-card">
            <p className="community-skeleton">{t('admin.loadingPosts', 'Loading posts...')}</p>
          </div>
        ) : !canEditCommunity ? (
          <div className="community-public-card">
            <h2 className="community-page-title">{t('admin.publish.title', 'Create update')}</h2>
            <p className="community-message error" style={{ marginTop: 14 }}>{uiText("Community editor access is required to publish updates.")}</p>
          </div>
        ) : (
          <>
            <div className="community-publish-hero">
              <div className="community-publish-header-row">
                <button
                  type="button"
                  className="community-btn secondary"
                  onClick={() => navigate('/community')}
                >
                  <i className="fa-solid fa-arrow-left" style={{ marginInlineEnd: 8 }} />
                  {t('admin.publish.backToFeed', 'Back to feed')}
                </button>

                <div className="community-publish-tags">
                  {channelOptions
                    .filter((channel) => availableFeeds.includes(channel.value))
                    .map((channel) => (
                      <span key={channel.value} className="community-badge">{uiText(channel.label)}</span>
                    ))}
                </div>
              </div>

              <h2 className="community-page-title">{t('admin.publish.title', 'Create update')}</h2>
              <p className="community-page-subtitle">
                {t('admin.publish.subtitle', 'Compose your message once and publish it to the right private audience.')}
              </p>
            </div>

            {successMessage && <div className="community-message success">{uiText(successMessage)}</div>}

            <div className="community-publish-layout">
              <div className="community-publish-panel">
                <CommunityPostComposer
                  allowedChannels={availableFeeds}
                  channelOptions={channelOptions}
                  currentUser={currentUser}
                  onCancel={() => navigate('/community')}
                  onPosted={() => {
                    setSuccessMessage(t('admin.posts.publishedSuccess', 'Post published.'));
                    setTimeout(() => navigate('/community'), 700);
                  }}
                />
              </div>

              <aside className="community-publish-side">
                <div className="community-publish-side-card">
                  <h4>{t('admin.publish.checklistTitle', 'Before you publish')}</h4>
                  <div className="community-publish-checks">
                    <div className="community-publish-check-item">
                      <i className="fa-solid fa-circle-check" />
                      <span>{t('admin.publish.check1', 'Keep your update concise and easy to scan.')}</span>
                    </div>
                    <div className="community-publish-check-item">
                      <i className="fa-solid fa-circle-check" />
                      <span>{t('admin.publish.check2', 'Select only the relevant private thread(s).')}</span>
                    </div>
                    <div className="community-publish-check-item">
                      <i className="fa-solid fa-circle-check" />
                      <span>{t('admin.publish.check3', 'Use image/video only when it adds context.')}</span>
                    </div>
                  </div>
                </div>

                <div className="community-publish-side-card">
                  <h4>{t('admin.publish.visibilityTitle', 'Visibility')}</h4>
                  <div className="community-publish-visibility-row">
                    <span className="community-badge">{uiText("Talent Pool")}</span>
                    <p>
                      {availableFeeds.includes('talent_pool')
                        ? t('admin.publish.visibilityTalent', 'Applicants and imported candidates in the talent pipeline.')
                        : uiText("Not assigned to your editor access.")}
                    </p>
                  </div>
                  <div className="community-publish-visibility-row">
                    <span className="community-badge">{uiText("Workforce")}</span>
                    <p>
                      {availableFeeds.includes('workforce')
                        ? t('admin.publish.visibilityTeam', 'Onboarding hires and current employees.')
                        : uiText("Not assigned to your editor access.")}
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommunityPublishPage;
